import { useMemo } from 'react';

import { Offer } from '@work4all/models/lib/Classes/Offer.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { EntityTableV8, IEntityTableV8 } from '../entity-table/EntityTableV8';
import { useEntityDataTableV8 } from '../entity-table/use-entity-data-table-v8';
import { ErpRessourceClassCell } from '../ErpRessourceClassCell';
import { useErpPlanningColumns } from '../planning-columns/use-erp-planning-columns';
import schema from '../schemata/offer-table-schema.json';
import { useErpHandlersTemplateV8 } from '../use-erp-handlers-template-v8';

import { useErpTable } from './erp-common/use-erp-table';
import { useCloneConvertErpActions } from './hooks/use-clone-convert-erp';

const defaultSort = [{ field: 'date', direction: SortDirection.DESCENDING }];

const secondarySort = [
  { field: 'number', direction: SortDirection.DESCENDING },
];

const forceRequestFields = {
  businessPartnerId: null,
  projectId: null,
  currencyId: null,
};

export function OffersTable(props: IEntityTableV8) {
  const ressourceClassColumns = useErpPlanningColumns({
    entity: Entities.offer,
  });

  const transformedSchema = useMemo(() => {
    return {
      ...schema,
      columns: [...schema.columns, ...ressourceClassColumns],
    } as never;
  }, [ressourceClassColumns]);

  const dataTable = useEntityDataTableV8<Offer, never>({
    ...props,
    schema: transformedSchema as never,
    cells: {
      ErpRessourceClass: ErpRessourceClassCell,
    },
    defaultSort,
    secondarySort,
    enableFooter: true,
    rowModifiers: (value) => {
      return {
        isFaded: value.isClosed && value.frozen,
        isShade1: !value.isClosed && value.frozen,
      };
    },
    forceRequestFields,
  });

  const template = useErpHandlersTemplateV8(dataTable.table);

  const { actions } = useCloneConvertErpActions({
    entity: Entities.offer,
    data: dataTable.selectedEntities,
  });

  useErpTable(Entities.offer, dataTable.data.length === 0);

  const custom = useMemo(
    () => ({
      left: actions,
    }),
    [actions]
  );

  return (
    <EntityTableV8
      {...props}
      {...dataTable}
      template={template}
      actions={{ custom }}
      displayFooter
    />
  );
}
